/* global axios */
import { useAlert } from 'dashboard/composables';

// All sandbox paths are forwarded through /api/v1/accounts/:id/proxy/knowledge,
// same pattern as store/modules/knowledge.js and onboarding.js.
const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

// POST /chat now only enqueues the chat turn (backend runs KB retrieval + LLM
// generation on a Celery queue instead of the request thread, since a single
// turn can take up to ~30s) and returns { job_id, status: "pending" }. This
// polls GET /sandbox/:id/jobs/:jobId until the backend reports done/failed.
// No websocket — sandbox traffic is one tester at a time, so plain polling is
// simplest and matches the KB ingest status-polling pattern already used
// elsewhere in this dashboard.
const SANDBOX_JOB_POLL_INTERVAL_MS = 1500;
const SANDBOX_JOB_POLL_TIMEOUT_MS = 120000;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const pollSandboxJob = async (accountId, jobId) => {
  const deadline = Date.now() + SANDBOX_JOB_POLL_TIMEOUT_MS;
  // The job is never done synchronously — always wait one interval before
  // the first poll rather than spending a round trip confirming "pending".
  while (Date.now() < deadline) {
    await sleep(SANDBOX_JOB_POLL_INTERVAL_MS);
    const { data } = await axios.get(
      knowledgeProxy(accountId, `/sandbox/${accountId}/jobs/${jobId}`)
    );
    if (data.status !== 'pending') return data;
  }
  throw new Error('sandbox_job_timeout');
};

export const state = {
  messages: [],
  datasetId: null,
  creditsUsed: 0,
  creditLimit: 30,
  creditsRemaining: 30,
  hasTested: false,
  // null = not yet resolved (still loading status), true/false once known
  isBlocked: null,
  isLoadingStatus: false,
  isLoadingMessages: false,
  isSending: false,
  isError: false,
  quotaExceeded: false,
  suggestions: [],
  isLoadingSuggestions: false,
  instruction: '',
  isLoadingInstruction: false,
  isSavingInstruction: false,
  selectedModel: null,
  isLoadingModel: false,
  isSavingModel: false,
  llmList: [],
  isLoadingLlmList: false,
};

export const getters = {
  messages: state => state.messages,
  datasetId: state => state.datasetId,
  creditsUsed: state => state.creditsUsed,
  creditLimit: state => state.creditLimit,
  creditsRemaining: state => state.creditsRemaining,
  hasTested: state => state.hasTested,
  isBlocked: state => state.isBlocked,
  isLoadingStatus: state => state.isLoadingStatus,
  isLoadingMessages: state => state.isLoadingMessages,
  isSending: state => state.isSending,
  isError: state => state.isError,
  quotaExceeded: state => state.quotaExceeded,
  suggestions: state => state.suggestions,
  isLoadingSuggestions: state => state.isLoadingSuggestions,
  instruction: state => state.instruction,
  isLoadingInstruction: state => state.isLoadingInstruction,
  isSavingInstruction: state => state.isSavingInstruction,
  selectedModel: state => state.selectedModel,
  isLoadingModel: state => state.isLoadingModel,
  isSavingModel: state => state.isSavingModel,
  llmList: state => state.llmList,
  isLoadingLlmList: state => state.isLoadingLlmList,
};

export const mutations = {
  SET_MESSAGES(state, messages) {
    state.messages = messages;
  },
  APPEND_MESSAGE(state, message) {
    state.messages.push(message);
  },
  SET_STATUS(
    state,
    { datasetId, creditsUsed, creditLimit, creditsRemaining, hasTested }
  ) {
    if (datasetId !== undefined) state.datasetId = datasetId;
    if (creditsUsed !== undefined) state.creditsUsed = creditsUsed;
    if (creditLimit !== undefined) state.creditLimit = creditLimit;
    if (creditsRemaining !== undefined)
      state.creditsRemaining = creditsRemaining;
    if (hasTested !== undefined) state.hasTested = hasTested;
  },
  SET_BLOCKED(state, bool) {
    state.isBlocked = bool;
  },
  SET_LOADING_STATUS(state, bool) {
    state.isLoadingStatus = bool;
  },
  SET_LOADING_MESSAGES(state, bool) {
    state.isLoadingMessages = bool;
  },
  SET_SENDING(state, bool) {
    state.isSending = bool;
  },
  SET_ERROR(state, bool) {
    state.isError = bool;
  },
  SET_QUOTA_EXCEEDED(state, bool) {
    state.quotaExceeded = bool;
  },
  SET_SUGGESTIONS(state, suggestions) {
    state.suggestions = suggestions;
  },
  SET_LOADING_SUGGESTIONS(state, bool) {
    state.isLoadingSuggestions = bool;
  },
  UPDATE_MESSAGE_RATING(state, { messageId, rating }) {
    const message = state.messages.find(m => m.id === messageId);
    if (message) message.rating = rating;
  },
  SET_INSTRUCTION(state, instruction) {
    state.instruction = instruction;
  },
  SET_LOADING_INSTRUCTION(state, bool) {
    state.isLoadingInstruction = bool;
  },
  SET_SAVING_INSTRUCTION(state, bool) {
    state.isSavingInstruction = bool;
  },
  SET_SELECTED_MODEL(state, model) {
    state.selectedModel = model;
  },
  SET_LOADING_MODEL(state, bool) {
    state.isLoadingModel = bool;
  },
  SET_SAVING_MODEL(state, bool) {
    state.isSavingModel = bool;
  },
  SET_LLM_LIST(state, llmList) {
    state.llmList = llmList;
  },
  SET_LOADING_LLM_LIST(state, bool) {
    state.isLoadingLlmList = bool;
  },
  RESET_SANDBOX_STATE(state) {
    state.messages = [];
    state.datasetId = null;
    state.creditsUsed = 0;
    state.creditsRemaining = state.creditLimit;
    state.hasTested = false;
    state.isBlocked = null;
    state.quotaExceeded = false;
  },
};

export const actions = {
  async fetchStatus({ commit, rootGetters }) {
    commit('SET_LOADING_STATUS', true);
    commit('SET_ERROR', false);

    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/sandbox/${accountId}/status`)
      );

      commit('SET_STATUS', {
        datasetId: data.dataset_id,
        creditsUsed: data.credits_used,
        creditLimit: data.credit_limit,
        creditsRemaining: data.credits_remaining,
        hasTested: data.has_tested,
      });
      commit('SET_BLOCKED', !data.dataset_id);
    } catch (error) {
      // Fail closed — if we can't confirm a dataset exists, don't show a chat
      // UI that will immediately 422.
      commit('SET_BLOCKED', true);
      commit('SET_ERROR', true);
      useAlert('Could not load sandbox status.');
      throw error.response?.data || error;
    } finally {
      commit('SET_LOADING_STATUS', false);
    }
  },

  // /sandbox/:id/status only reports a dataset_id once the backend's sandbox
  // *session* has one — that only happens after the first successful /chat
  // call includes one. So right after finishing step 1, status legitimately
  // has nothing yet, even though the dataset itself exists — call this to
  // resolve one before treating step 2 as blocked. Tries, in order: a
  // same-session id passed in from onboardingKnowledge (fastest, no extra
  // call), then the account's dataset list (survives a refresh, which clears
  // onboardingKnowledge's in-memory state same as this module's).
  async ensureDatasetId({ commit, state, rootGetters }, fallbackDatasetId) {
    if (state.datasetId) return state.datasetId;

    if (fallbackDatasetId) {
      commit('SET_STATUS', { datasetId: fallbackDatasetId });
      commit('SET_BLOCKED', false);
      return fallbackDatasetId;
    }

    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets`)
      );
      // UNVERIFIED against a live response — this endpoint's response schema
      // is untyped in openapi.json, unlike the two sibling fields (dataset
      // create, document create) that turned out not to match their assumed
      // shape either. Tries the plausible list locations and id keys rather
      // than assuming one; confirm the real shape against Network tab if
      // this doesn't resolve a dataset on a refreshed step 2.
      const list = Array.isArray(data)
        ? data
        : data?.data || data?.datasets || [];
      if (!list.length) return null;

      const latest = [...list].sort(
        (a, b) => new Date(b.created_at ?? 0) - new Date(a.created_at ?? 0)
      )[0];
      const datasetId = latest?.id || latest?.dataset_id;
      if (!datasetId) return null;

      commit('SET_STATUS', { datasetId });
      commit('SET_BLOCKED', false);
      return datasetId;
    } catch (error) {
      return null;
    }
  },

  async fetchMessages({ commit, rootGetters }) {
    commit('SET_LOADING_MESSAGES', true);
    commit('SET_ERROR', false);

    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/sandbox/${accountId}/messages`)
      );

      // The transcript endpoint now returns chunks too (previously always
      // empty here, so restored history couldn't show citations — only
      // messages sent in the current live turn could). Same shape as the
      // live /chat response's chunks, rendered the same way by
      // SandboxBotMessage/SandboxCitations regardless of source. User
      // messages come back with chunks: null, so normalize to [].
      const messages = (data || []).map(message => ({
        id: message.id,
        role: message.role,
        content: message.content,
        rating: message.rating || null,
        chunks: message.chunks || [],
        createdAt: message.created_at,
      }));

      commit('SET_MESSAGES', messages);
    } catch (error) {
      commit('SET_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_LOADING_MESSAGES', false);
    }
  },

  async sendMessage({ commit, state, rootGetters }, messageText) {
    if (state.isBlocked) return;

    const accountId = rootGetters.getCurrentAccountId;

    commit('APPEND_MESSAGE', {
      id: `local-${Date.now()}`,
      role: 'user',
      content: messageText,
      rating: null,
      chunks: [],
      createdAt: new Date().toISOString(),
    });

    commit('SET_SENDING', true);
    commit('SET_ERROR', false);

    try {
      const body = { message: messageText };
      if (state.datasetId) body.dataset_id = state.datasetId;

      const { data: ack } = await axios.post(
        knowledgeProxy(accountId, `/sandbox/${accountId}/chat`),
        body
      );

      const job = await pollSandboxJob(accountId, ack.job_id);

      if (job.status === 'failed') {
        if (job.error === 'sandbox_quota_exceeded') {
          commit('SET_QUOTA_EXCEEDED', true);
          commit('APPEND_MESSAGE', {
            id: `local-quota-${Date.now()}`,
            role: 'assistant',
            content:
              "Looks like you've used up your sandbox demo messages. Connect a channel below when you're ready to keep the conversation going — I'll be right here.",
            rating: null,
            chunks: [],
            createdAt: new Date().toISOString(),
          });
          return;
        }
        if (job.error === 'dataset_not_configured') {
          // Defensive fallback — the component's mount guard should already
          // prevent reaching the composer without a datasetId.
          commit('SET_BLOCKED', true);
          useAlert('Please finish Step 1 first.');
          return;
        }
        if (job.error === 'llm_unavailable') {
          useAlert('The assistant is temporarily unavailable — try again.');
        }
        commit('SET_ERROR', true);
        throw job;
      }

      commit('APPEND_MESSAGE', {
        id: job.message_id,
        role: 'assistant',
        content: job.reply,
        rating: null,
        chunks: job.chunks || [],
        createdAt: new Date().toISOString(),
      });
      commit('SET_STATUS', {
        creditsUsed: job.credits_used,
        creditsRemaining: job.credits_remaining,
      });
    } catch (error) {
      // A `throw job` above (job.status === 'failed') already alerted where
      // applicable and committed SET_ERROR — this only needs to avoid
      // double-committing it and rethrow. Anything else reaching here is a
      // genuinely unexpected failure: a network error on the POST/poll GET,
      // or the poll timing out.
      if (error?.message === 'sandbox_job_timeout') {
        useAlert(
          'This is taking longer than expected — try again in a moment.'
        );
      }
      if (error?.status !== 'failed') {
        commit('SET_ERROR', true);
      }
      throw error.response?.data || error;
    } finally {
      commit('SET_SENDING', false);
    }
  },

  async fetchSuggestions({ commit, rootGetters }) {
    commit('SET_LOADING_SUGGESTIONS', true);

    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/sandbox/${accountId}/suggestions`)
      );
      commit('SET_SUGGESTIONS', data?.questions || []);
    } catch (error) {
      // Non-critical — render nothing rather than blocking the sandbox,
      // matching the 200-with-empty-array behavior the endpoint itself uses
      // when generation fails.
      commit('SET_SUGGESTIONS', []);
    } finally {
      commit('SET_LOADING_SUGGESTIONS', false);
    }
  },

  async fetchInstruction({ commit, rootGetters }) {
    commit('SET_LOADING_INSTRUCTION', true);
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/sandbox/${accountId}/instruction`)
      );
      commit('SET_INSTRUCTION', data?.instruction || '');
    } catch (error) {
      // Non-critical — leave the field empty rather than blocking the panel.
    } finally {
      commit('SET_LOADING_INSTRUCTION', false);
    }
  },

  async updateInstruction({ commit, rootGetters }, instruction) {
    commit('SET_SAVING_INSTRUCTION', true);
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data } = await axios.put(
        knowledgeProxy(accountId, `/sandbox/${accountId}/instruction`),
        { instruction }
      );
      commit('SET_INSTRUCTION', data?.instruction ?? instruction);
      return data;
    } catch (error) {
      throw error.response?.data || error;
    } finally {
      commit('SET_SAVING_INSTRUCTION', false);
    }
  },

  async fetchSandboxModel({ commit, rootGetters }) {
    commit('SET_LOADING_MODEL', true);
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/sandbox/${accountId}/model`)
      );
      commit('SET_SELECTED_MODEL', data?.model || null);
    } catch (error) {
      // Non-critical — leave unset rather than blocking the panel.
    } finally {
      commit('SET_LOADING_MODEL', false);
    }
  },

  async updateSandboxModel({ commit, state, rootGetters }, model) {
    const previousModel = state.selectedModel;

    // Optimistic — a dropdown pick should feel instant, same as rateMessage
    // below. Rolled back on failure.
    commit('SET_SELECTED_MODEL', model);
    commit('SET_SAVING_MODEL', true);
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data } = await axios.put(
        knowledgeProxy(accountId, `/sandbox/${accountId}/model`),
        { model }
      );
      commit('SET_SELECTED_MODEL', data?.model ?? model);
      return data;
    } catch (error) {
      commit('SET_SELECTED_MODEL', previousModel);
      throw error.response?.data || error;
    } finally {
      commit('SET_SAVING_MODEL', false);
    }
  },

  // Same endpoint and shape as ChatBotConfiguration's getAvailableLlm — the
  // sandbox model picker now offers the same LLM catalogue as the inbox
  // chatbot config page, instead of a hardcoded 3-model list.
  async fetchAvailableModels({ commit, rootGetters }) {
    commit('SET_LOADING_LLM_LIST', true);
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, '/chatbot/available_llm')
      );
      const data = response.data;
      const all = data.flatMap(provider =>
        provider.models.map(model => ({
          name: model.model,
          provider: provider.provider,
          providerLabel: provider.label?.en_US || provider.provider,
          displayName: `${model.model} (${
            provider.label?.en_US || provider.provider
          })`,
          creditCost: model.credit_cost,
        }))
      );
      commit('SET_LLM_LIST', all);
    } catch (error) {
      // Non-critical — leave empty rather than blocking the panel.
      commit('SET_LLM_LIST', []);
    } finally {
      commit('SET_LOADING_LLM_LIST', false);
    }
  },

  // Marks where the persona changed mid-conversation. Existing messages are
  // deliberately left in place rather than cleared — there's no self-service
  // way to do that (see resetSandbox above), so a visible divider is the
  // next best thing: it makes clear which replies reflect the old vs the
  // updated instructions, instead of leaving that ambiguous. No-ops before
  // the first message, since there's nothing yet to disambiguate.
  noteInstructionUpdated({ commit, state }, content) {
    if (!state.messages.length) return;
    commit('APPEND_MESSAGE', {
      id: `local-note-${Date.now()}`,
      role: 'system',
      content,
      rating: null,
      chunks: [],
      createdAt: new Date().toISOString(),
    });
  },

  async rateMessage({ commit, rootGetters }, { messageId, rating, reason }) {
    const accountId = rootGetters.getCurrentAccountId;

    // Optimistic — a thumbs click should feel instant.
    commit('UPDATE_MESSAGE_RATING', { messageId, rating });

    try {
      await axios.post(
        knowledgeProxy(
          accountId,
          `/sandbox/${accountId}/messages/${messageId}/feedback`
        ),
        { rating, reason }
      );
    } catch (error) {
      // Roll back on failure.
      commit('UPDATE_MESSAGE_RATING', { messageId, rating: null });
      throw error.response?.data || error;
    }
  },

  // Deliberately not wired to any UI — the endpoint's own spec describes it
  // as "Ops-only: clear an account's sandbox transcript and quota counter",
  // not a self-service action (a user-triggerable reset would let someone
  // refresh their 30-message trial quota indefinitely). Kept for a future
  // internal/support tool, not the onboarding wizard.
  async resetSandbox({ commit, rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;

    try {
      await axios.post(
        knowledgeProxy(accountId, `/sandbox/${accountId}/reset`)
      );
      commit('RESET_SANDBOX_STATE');
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
